import type { IAgingClient, IAgingInvoice } from 'src/types';

/**
 * What the client aging table shows, as words — one definition, read by the table on screen and
 * by its XLSX export (NAT-2481), so the file cannot say something the screen does not.
 */

/**
 * A forecast row that is already past due is not paperwork in flight — it is an order carrying
 * a stale `invoiced` flag, and calling it "da emettere" asserts something the data contradicts.
 * Measured on the live report: 56 of 63 forecast rows sit in the 60+ bucket, the oldest ordered
 * in 2017. They get a label that reads as the data problem it is.
 */
export const basisLabelKey = (invoice: IAgingInvoice) => {
  if (invoice.amountBasis === 'excluded') return 'non_conteggiata';
  return invoice.daysOverdue > 0 ? 'fattura_mancante' : 'da_emettere';
};

/**
 * The note printed under an invoice number: how the amount was arrived at when it is not an
 * issued invoice's open amount, or «da emettere» for a pre-NAT-2261 row that has neither a number
 * nor a basis. Null for an ordinary invoice.
 */
export const invoiceNoteKey = (invoice: IAgingInvoice): string | null => {
  if (invoice.amountBasis && invoice.amountBasis !== 'invoice') return basisLabelKey(invoice);
  if (!invoice.invoiceNumber && !invoice.amountBasis) return 'da_emettere';
  return null;
};

/** One invoice can cover several orders; a pre-NAT-2261 row carries a single `orderCode`. */
export const orderCodesOf = (invoice: IAgingInvoice) =>
  invoice.orderCodes ?? (invoice.orderCode ? [invoice.orderCode] : []);

/** The open amount; a pre-NAT-2261 backend sends the order total instead. */
export const openAmountOf = (invoice: IAgingInvoice) => invoice.amountOpen ?? invoice.totalEndPrice;

/**
 * A priority that rests on the revenue floor rather than on the client's revenue: a rank, not a
 * measurement. The screen marks it with an asterisk; the file says it in words.
 */
export const priorityOnFloor = (client: IAgingClient) =>
  client.priorityScore !== undefined && client.priorityScoreBasis !== 'revenue';

const NO_VALUE = '—';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * NAT-2800, rule 55 — «53 gg · 1 pagamento». The count is passed as `count` so the language's own
 * plural form applies; a client with no payment in the window (null, or a backend that does not
 * send it) reads «—», never a zero.
 */
export const paymentTimeLabel = (client: IAgingClient, t: Translate): string =>
  client.paymentTime && client.paymentTime.days !== null
    ? t('pagamento_medio_valore', {
        days: client.paymentTime.days,
        count: client.paymentTime.payments,
      })
    : NO_VALUE;

/**
 * The terms in days when the label can state them: days AND a source. Anything less reads «—», so
 * the sort uses this too and a «—» row never sorts as a figure.
 */
export const termsDaysOf = (client: IAgingClient): number | null => {
  const time = client.paymentTime;
  if (!time || !time.termsSource) return null;
  return time.termsDays ?? null;
};

/** True when the terms shown were read off the invoices — the one case that carries a note. */
export const termsFromInvoices = (client: IAgingClient): boolean =>
  termsDaysOf(client) !== null && client.paymentTime?.termsSource === 'in_fattura';

/** True when some row has a payment: «null» (no payment in the window) is not a payment. */
export const anyPaymentTime = (clients: IAgingClient[]): boolean =>
  clients.some((c) => c.paymentTime != null);

/** «30 gg concordati» when the terms are agreed, «26 gg in fattura» when read off the invoices. */
export const termsLabel = (client: IAgingClient, t: Translate): string => {
  const days = termsDaysOf(client);
  if (days === null) return NO_VALUE;
  return t(
    client.paymentTime?.termsSource === 'concordati' ? 'termini_concordati' : 'termini_in_fattura',
    { days },
  );
};
